import {
  queryOptions,
  useMutation,
  useQueryClient,
  type UseMutationResult,
} from '@tanstack/react-query';

import { getAuthHeaders } from '@/identity.js';
import { apiFetch } from '@/lib/api-fetch.js';
import { notify } from '@/lib/notify.js';
import { optionalSessionJwt } from '@/lib/sso.js';

/**
 * Client for artifact sharing beyond the workspace — the file half of what `lib/api/shares.ts` does for
 * chat snapshots, deliberately the same shape and the same vocabulary.
 *
 * Three prefixes, because they answer to three different audiences. Managing a file's shares stays under
 * the workspace prefix the artifact already lives on; the share itself is addressed on its own top-level
 * `/v1/artifact-shares/:id`, where no workspace id appears by construction — the recipient does not know,
 * and must not learn, which workspace the file came from; and `/v1/shared-with-me` answers for whoever
 * holds the bearer, listing what is waiting for them and nothing about where it came from.
 *
 * The wire is snake_case both ways (`access_mode`, `subject_type`, `already_claimed`) and the service
 * validates with a whitelist, so those exact keys are asserted in the tests rather than trusted to the
 * types.
 */

/**
 * How wide a share is open: the people named on it, every Customertimes employee holding the link, or
 * anyone at all — `public` being the only one that serves a reader with no session.
 */
export type ArtifactShareAccessMode = 'named' | 'internal' | 'public';

/** What a recipient may do with the file. A share hands over a copy, and only ever a copy. */
export type ArtifactSharePermission = 'copy';

/** Who a `named` share is addressed to. Phase 1 addresses people; Entra groups are phase 3. */
export type ArtifactShareSubjectType = 'user' | 'entra_group';

/** One recipient of a share. `displayName` is null when the directory could not resolve the id. */
export interface ArtifactShareSubject {
  subjectType: ArtifactShareSubjectType;
  subjectId: string;
  displayName: string | null;
}

/** Somebody who has already taken their copy. Their copy is theirs — revoking the share cannot reach it. */
export interface ArtifactShareClaim {
  userId: string;
  displayName: string | null;
  claimedAt: string;
}

/** One share of one file, as create and list both return it. */
export interface ArtifactShareView {
  id: string;
  artifactId: string;
  accessMode: ArtifactShareAccessMode;
  permission: ArtifactSharePermission;
  /** Owner-chosen label, or null when the share was created without one. */
  name: string | null;
  subjects: ArtifactShareSubject[];
  expiresAt: string | null;
  revokedAt: string | null;
  createdAt: string;
  createdBy: string;
  claims: ArtifactShareClaim[];
  /** When the Teams notification went out, or null when none ever did. */
  notifiedAt: string | null;
  /** A short human reason the notification did not arrive, or null when nothing went wrong. */
  notifyError: string | null;
}

/**
 * Why a share is visible to the caller in "Shared with me": addressed to them by name, or through a
 * group they are in. It answers the question a recipient actually asks about an unexpected file.
 */
export type SharedWithMeReason = 'named' | 'group';

/**
 * One file waiting for the caller — a share addressed to them that they have not taken yet.
 *
 * This is the primary way a recipient finds out that a file is waiting: the Teams message on top of it
 * can only reach somebody who has the Secretary app installed, which is nobody's guarantee. Taking the
 * copy removes the share from this list, because from then on it is an ordinary file in their artifacts.
 *
 * The workspace the file came from is not here, and must not be added: it is often a client or a project
 * name, and the recipient is not a member of it.
 */
export interface SharedWithMeShare {
  id: string;
  fileName: string;
  contentType: string;
  /** Null when the size is unknown — never 0, which would read as an empty file. */
  size: number | null;
  /** Null when identity could not resolve who shared it. */
  sharedByName: string | null;
  sharedAt: string;
  reason: SharedWithMeReason;
}

/**
 * What the recipient of a link is told before they take anything: the file, who shared it, and which one
 * button they get. The source workspace's NAME is never on this card — it is often a client or a project
 * name, and it stays inside the workspace it belongs to.
 */
export interface ArtifactShareCard {
  id: string;
  accessMode: ArtifactShareAccessMode;
  fileName: string;
  contentType: string;
  size: number;
  sharedByName: string | null;
  sharedAt: string | null;
  /** Set once this caller has taken their copy — the button then opens that copy. */
  claimedArtifactId: string | null;
  /** Set when the caller already sees the original by the ordinary access rules. */
  originalArtifactId: string | null;
  originalWorkspaceId: string | null;
  canClaim: boolean;
}

/**
 * What reading a card can come back as.
 *
 * `needs-auth` is kept apart from `unavailable` on purpose: a 401 means the link is fine and the reader
 * is not signed in, which is an invitation to sign in and never a refusal. Everything else unavailable —
 * missing, revoked, expired, not addressed to this caller — collapses into one answer, because telling
 * those apart would confirm that the file exists.
 */
export type ArtifactShareCardResult =
  | { kind: 'card'; card: ArtifactShareCard }
  | { kind: 'needs-auth' }
  | { kind: 'unavailable' };

/** The answer to a claim. Idempotent: a repeat names the same copy with `alreadyClaimed` set. */
export interface ArtifactClaimResult {
  artifactId: string;
  workspaceId: string;
  alreadyClaimed: boolean;
}

interface ArtifactShareSubjectWire {
  subject_type: ArtifactShareSubjectType;
  subject_id: string;
  display_name: string | null;
}

interface ArtifactShareClaimWire {
  user_id: string;
  display_name: string | null;
  claimed_at: string;
}

interface ArtifactShareViewWire {
  id: string;
  artifact_id: string;
  access_mode: ArtifactShareAccessMode;
  permission: ArtifactSharePermission;
  name: string | null;
  subjects: ArtifactShareSubjectWire[];
  expires_at: string | null;
  revoked_at: string | null;
  created_at: string;
  created_by: string;
  claims: ArtifactShareClaimWire[];
  notified_at?: string | null;
  notify_error?: string | null;
}

interface SharedWithMeShareWire {
  id: string;
  file_name: string;
  content_type: string;
  size: number | null;
  shared_by_name: string | null;
  shared_at: string;
  reason: SharedWithMeReason;
}

interface ArtifactShareCardWire {
  id: string;
  access_mode: ArtifactShareAccessMode;
  file_name: string;
  content_type: string;
  size: number;
  shared_by_name: string | null;
  shared_at: string | null;
  claimed_artifact_id: string | null;
  original_artifact_id: string | null;
  original_workspace_id: string | null;
  can_claim: boolean;
}

/** The create body, spelled exactly as the service's DTO declares it. */
interface CreateArtifactShareBody {
  access_mode: ArtifactShareAccessMode;
  subjects: { subject_type: ArtifactShareSubjectType; subject_id: string }[];
  name: string | null;
  expires_at: string | null;
}

/** Platform-JWT bearer (zero-trust) + JSON content type, the idiom every artifact call here uses. */
async function authHeaders(): Promise<Record<string, string>> {
  return { 'Content-Type': 'application/json', ...(await getAuthHeaders()) };
}

/**
 * The bearer for a link whose holder may have no session at all — attached only when one is already in
 * the identity store.
 *
 * Not through `getAuthHeaders()` or `apiFetch`: both would drag an unauthenticated reader through the
 * Teams bootstrap and an Entra round-trip nobody asked for. `optionalSessionJwt` renews only a session
 * that already exists, so a signed-in reader whose token aged out is not taken for a stranger. Here an
 * absent bearer is a valid request and a 401 is a valid answer. Exactly what the chat-snapshot viewer
 * does, for exactly the same reason.
 */
async function optionalBearer(): Promise<Record<string, string>> {
  const jwt = await optionalSessionJwt();
  return jwt !== null && jwt.length > 0
    ? { Authorization: `Bearer ${jwt}` }
    : {};
}

/** Pull a human-readable message out of a non-ok response body. */
async function errorMessage(res: Response, fallback: string): Promise<string> {
  const text = await res.text().catch(() => '');
  try {
    const body = JSON.parse(text) as { message?: string | string[] };
    if (Array.isArray(body.message)) return body.message.join('; ');
    if (typeof body.message === 'string' && body.message.trim()) {
      return body.message;
    }
  } catch {
    if (text.trim()) return text.trim();
  }
  return `${fallback} (${res.status})`;
}

function shareFromWire(wire: ArtifactShareViewWire): ArtifactShareView {
  return {
    id: wire.id,
    artifactId: wire.artifact_id,
    accessMode: wire.access_mode,
    permission: wire.permission,
    name: wire.name,
    subjects: (wire.subjects ?? []).map((subject) => ({
      subjectType: subject.subject_type,
      subjectId: subject.subject_id,
      displayName: subject.display_name,
    })),
    expiresAt: wire.expires_at,
    revokedAt: wire.revoked_at,
    createdAt: wire.created_at,
    createdBy: wire.created_by,
    claims: (wire.claims ?? []).map((claim) => ({
      userId: claim.user_id,
      displayName: claim.display_name,
      claimedAt: claim.claimed_at,
    })),
    /**
     * Both null is the ordinary state, not a gap: a platform with no Secretary configured notifies
     * nobody, and so does a share addressed to a group. A server that predates the notification columns
     * sends neither key, which is the same fact and reads the same way.
     */
    notifiedAt: wire.notified_at ?? null,
    notifyError: wire.notify_error ?? null,
  };
}

/** Management route of one file's shares, under the workspace prefix the artifact already lives on. */
function sharesEndpoint(workspaceId: string, artifactId: string): string {
  return `/v1/workspaces/${encodeURIComponent(workspaceId)}/artifacts/${encodeURIComponent(artifactId)}/shares`;
}

async function fetchArtifactShares(
  workspaceId: string,
  artifactId: string,
): Promise<ArtifactShareView[]> {
  const res = await fetch(sharesEndpoint(workspaceId, artifactId), {
    headers: await authHeaders(),
  });
  if (!res.ok) {
    const message = await errorMessage(res, 'Failed to load the shares');
    notify.error(message);
    throw new Error(message);
  }
  const body = (await res.json()) as { shares?: ArtifactShareViewWire[] };
  return (body.shares ?? []).map(shareFromWire);
}

/** What the dialog hands over to create a share. `null` fields are sent, not omitted. */
export interface CreateArtifactShareVars {
  workspaceId: string;
  artifactId: string;
  accessMode: ArtifactShareAccessMode;
  subjects: {
    subjectType: ArtifactShareSubjectType;
    subjectId: string;
  }[];
  name?: string | null;
  expiresAt?: string | null;
}

async function createArtifactShare(
  vars: CreateArtifactShareVars,
): Promise<ArtifactShareView> {
  const trimmed = (vars.name ?? '').trim();
  const body: CreateArtifactShareBody = {
    access_mode: vars.accessMode,
    subjects: vars.subjects.map((subject) => ({
      subject_type: subject.subjectType,
      subject_id: subject.subjectId,
    })),
    name: trimmed.length > 0 ? trimmed : null,
    expires_at: vars.expiresAt ?? null,
  };
  const res = await fetch(sharesEndpoint(vars.workspaceId, vars.artifactId), {
    method: 'POST',
    headers: await authHeaders(),
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const message = await errorMessage(res, 'Failed to share the file');
    notify.error(message);
    throw new Error(message);
  }
  return shareFromWire((await res.json()) as ArtifactShareViewWire);
}

export interface RevokeArtifactShareVars {
  workspaceId: string;
  artifactId: string;
  shareId: string;
}

async function revokeArtifactShare(
  vars: RevokeArtifactShareVars,
): Promise<void> {
  const res = await fetch(
    `${sharesEndpoint(vars.workspaceId, vars.artifactId)}/${encodeURIComponent(vars.shareId)}`,
    { method: 'DELETE', headers: await authHeaders() },
  );
  if (!res.ok && res.status !== 204) {
    const message = await errorMessage(res, 'Failed to revoke the share');
    notify.error(message);
    throw new Error(message);
  }
}

/** Query-key factory, so a revoke refreshes exactly the file whose share went. */
export const artifactShareKeys = {
  list: (workspaceId: string, artifactId: string) =>
    ['artifact-shares', workspaceId, artifactId] as const,
};

/** TanStack Query factory for one file's shares; answers an empty list with no file in hand. */
export function artifactSharesQueryOptions(
  workspaceId: string,
  artifactId: string | null | undefined,
) {
  return queryOptions({
    queryKey: artifactShareKeys.list(workspaceId, artifactId ?? 'none'),
    queryFn: async (): Promise<ArtifactShareView[]> => {
      if (!artifactId) return [];
      return fetchArtifactShares(workspaceId, artifactId);
    },
    enabled: Boolean(artifactId),
    staleTime: 30_000,
  });
}

/**
 * Query key for the caller's own waiting shares. No workspace and no user in it: the endpoint answers for
 * whoever holds the bearer, and one key is what lets a claim anywhere in the app drop the taken row.
 */
export const sharedWithMeKeys = {
  list: () => ['shared-with-me'] as const,
};

/**
 * The files waiting for the caller — shares addressed to them, by name or through a group, that they have
 * not taken yet.
 *
 * No toast on failure, deliberately: the section shows its own message, and a query that also shouts
 * would say the same thing twice for something the reader did not ask for.
 */
async function fetchSharedWithMe(): Promise<SharedWithMeShare[]> {
  const res = await apiFetch('/v1/shared-with-me', {
    headers: { 'Content-Type': 'application/json' },
  });
  if (!res.ok) {
    throw new Error(
      await errorMessage(res, 'Failed to load the files shared with you'),
    );
  }
  const body = (await res.json()) as { shares?: SharedWithMeShareWire[] };
  return (body.shares ?? []).map((wire) => ({
    id: wire.id,
    fileName: wire.file_name,
    contentType: wire.content_type,
    size: wire.size ?? null,
    sharedByName: wire.shared_by_name ?? null,
    sharedAt: wire.shared_at,
    reason: wire.reason,
  }));
}

/** TanStack Query factory for "Shared with me". */
export function sharedWithMeQueryOptions() {
  return queryOptions({
    queryKey: sharedWithMeKeys.list(),
    queryFn: fetchSharedWithMe,
    staleTime: 30_000,
  });
}

/**
 * The address a recipient opens — an SPA route built from this origin, never a field of a response. The
 * interface lives on its own origin in every environment, and a link assembled anywhere else sends the
 * colleague somewhere else.
 */
export function artifactShareUrl(shareId: string): string {
  return `${window.location.origin}/shared/${encodeURIComponent(shareId)}`;
}

/**
 * Whether a share still opens. Revoked is final; an expiry is a moment, so it is compared against a
 * clock the caller can pin in a test.
 */
export function isArtifactShareActive(
  share: ArtifactShareView,
  now: number = Date.now(),
): boolean {
  if (share.revokedAt !== null) return false;
  if (share.expiresAt === null) return true;
  return Date.parse(share.expiresAt) > now;
}

export function useCreateArtifactShare(): UseMutationResult<
  ArtifactShareView,
  Error,
  CreateArtifactShareVars
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createArtifactShare,
    onSuccess: (_share, { workspaceId, artifactId }) => {
      void queryClient.invalidateQueries({
        queryKey: artifactShareKeys.list(workspaceId, artifactId),
      });
    },
  });
}

export function useRevokeArtifactShare(): UseMutationResult<
  void,
  Error,
  RevokeArtifactShareVars
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: revokeArtifactShare,
    onSuccess: (_data, { workspaceId, artifactId }) => {
      void queryClient.invalidateQueries({
        queryKey: artifactShareKeys.list(workspaceId, artifactId),
      });
    },
  });
}

/**
 * Where the bytes of a shared file come from.
 *
 * A URL rather than a fetch: the service answers it with `Content-Disposition: attachment`, so the
 * browser saves the file on its own — no bearer to attach and no blob to build, which is all an
 * anonymous reader of a `public` share has to work with.
 */
export function artifactShareContentUrl(shareId: string): string {
  return `/v1/artifact-shares/${encodeURIComponent(shareId)}/content`;
}

/**
 * The shared file, for a page that draws it rather than saving it: the original bytes as a `Blob`, so
 * Download saves them untouched, and their text for the preview. A fetch reads the body whatever
 * `Content-Disposition` says, and the service answers it through the same gate as the card, so any reader
 * the card opened for gets the bytes too. Same optional bearer as the card, for the same reason.
 */
export async function fetchArtifactShareFile(
  shareId: string,
): Promise<{ blob: Blob; text: string }> {
  const res = await fetch(artifactShareContentUrl(shareId), {
    headers: await optionalBearer(),
    cache: 'no-store',
    credentials: 'omit',
  });
  if (!res.ok)
    throw new Error(`Failed to load the shared file (${res.status})`);
  const bytes = await res.arrayBuffer();
  return {
    blob: new Blob([bytes], {
      type: res.headers.get('content-type') ?? 'text/html',
    }),
    text: new TextDecoder().decode(bytes),
  };
}

/**
 * Read the card behind a link.
 *
 * Never fails for the want of a session: the bearer goes out only if one exists, and a link opened with
 * none answers either with the card (a `public` share) or with `needs-auth`.
 */
export async function fetchArtifactShareCard(
  shareId: string,
): Promise<ArtifactShareCardResult> {
  const res = await fetch(
    `/v1/artifact-shares/${encodeURIComponent(shareId)}`,
    {
      headers: await optionalBearer(),
      cache: 'no-store',
      credentials: 'omit',
    },
  );
  if (res.status === 401) return { kind: 'needs-auth' };
  if (!res.ok) return { kind: 'unavailable' };
  const wire = (await res.json()) as ArtifactShareCardWire;
  const card: ArtifactShareCard = {
    id: wire.id,
    accessMode: wire.access_mode,
    fileName: wire.file_name,
    contentType: wire.content_type,
    size: wire.size,
    sharedByName: wire.shared_by_name,
    sharedAt: wire.shared_at,
    claimedArtifactId: wire.claimed_artifact_id,
    originalArtifactId: wire.original_artifact_id,
    originalWorkspaceId: wire.original_workspace_id,
    canClaim: wire.can_claim,
  };
  return { kind: 'card', card };
}

/**
 * Take the copy. The service is idempotent, so a second call names the copy the first one made rather
 * than making another.
 *
 * Goes through `apiFetch` because this call is reached from a page that renders outside the sign-in gate:
 * the token is taken from the renewal core, so a claim needs no identity bootstrap behind it and still
 * survives a token that died between being attached and being read.
 */
export async function claimArtifactShare(
  shareId: string,
): Promise<ArtifactClaimResult> {
  const res = await apiFetch(
    `/v1/artifact-shares/${encodeURIComponent(shareId)}/claim`,
    { method: 'POST', headers: { 'Content-Type': 'application/json' } },
  );
  if (!res.ok) {
    const message = await errorMessage(res, 'Failed to save the file');
    notify.error(message);
    throw new Error(message);
  }
  const wire = (await res.json()) as {
    artifact_id: string;
    workspace_id: string;
    already_claimed: boolean;
  };
  return {
    artifactId: wire.artifact_id,
    workspaceId: wire.workspace_id,
    alreadyClaimed: wire.already_claimed,
  };
}
